import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useT } from "../../shared/i18n";
import { SCANNER_DISPLAYS, usePreferencesStore } from "../../shared/store/preferences";
import { SCANNER_DISPLAY_KEYS, ScanStage, type ScanStageProps } from "./ScanStage";

interface ScanViewfinderProps extends Omit<ScanStageProps, "hidden" | "width"> {
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  /**
   * Closed-chip width. Defaults to 112 px; pass `"100%"` inside a sized
   * wrapper for the `sm+` side-by-side stage.
   */
  chipWidth?: number | string;
}

/**
 * The global preset's viewfinder (scanner-surface 05).
 *
 * Closed it is a 112 px square chip in the result header; the chip itself is
 * the aim affordance (`role="button"`, keyboard operable), so there is no
 * separate "Aim" button. Open it becomes a full-screen aiming view with the
 * ROI frame, the three persisted display-mode chips and a close action.
 *
 * Crucially the stage is the *same* mounted element in both states — only its
 * width and the surrounding chrome change — so the camera loop never restarts.
 */
export function ScanViewfinder({
  expanded,
  onExpand,
  onCollapse,
  chipWidth = 112,
  ...stage
}: ScanViewfinderProps) {
  const t = useT();
  const display = usePreferencesStore((state) => state.scannerDisplay);
  const setDisplay = usePreferencesStore((state) => state.setScannerDisplay);
  const numericChip = typeof chipWidth === "number";

  const open = () => {
    if (!expanded) onExpand();
  };

  return (
    <Box
      data-testid="scan-viewfinder"
      data-expanded={expanded ? "true" : "false"}
      data-mode={display}
      role={expanded ? undefined : "button"}
      tabIndex={expanded ? undefined : 0}
      aria-label={expanded ? undefined : t("scanAim")}
      aria-expanded={expanded}
      onClick={expanded ? undefined : open}
      onKeyDown={(event) => {
        if (expanded) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
      sx={
        expanded
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1400,
              bgcolor: "rgba(8,10,12,0.94)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 2,
            }
          : {
              position: "relative",
              width: chipWidth,
              height: numericChip ? chipWidth : undefined,
              aspectRatio: numericChip ? undefined : "1/1",
              flexShrink: 0,
              borderRadius: 2,
              overflow: "hidden",
              cursor: "pointer",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }
      }
    >
      <Box sx={{ width: expanded ? "min(88vw, 380px)" : "100%", flexShrink: 0 }}>
        <ScanStage {...stage} width={expanded ? "100%" : chipWidth} />
      </Box>

      {expanded && (
        <Box
          data-testid="scan-aim"
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: "wrap", justifyContent: "center" }}
          >
            {SCANNER_DISPLAYS.map((entry) => (
              <Chip
                key={entry}
                label={t(SCANNER_DISPLAY_KEYS[entry])}
                data-testid={`scan-display-${entry}`}
                onClick={() => setDisplay(entry)}
                sx={{
                  bgcolor: display === entry ? "primary.main" : "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: 0,
                }}
              />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", textAlign: "center" }}>
            {t("scanAimPersisted")}
          </Typography>
          <Button
            autoFocus
            variant="contained"
            onClick={onCollapse}
            data-testid="scan-aim-close"
          >
            {t("scanCloseAim")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
